import { ALL_FORMATS, BlobSource, Input } from "mediabunny";
import type { AudioMetadata } from "../types/project";
import { readIsoBmffMovieDuration } from "./isobmff";

export type OpenedAudio = {
  input: Input;
  metadata: AudioMetadata;
};

export async function readAudioMetadata(file: File): Promise<AudioMetadata> {
  const input = new Input({
    formats: ALL_FORMATS,
    source: new BlobSource(file, { maxCacheSize: 4 * 1024 * 1024 }),
  });
  try {
    if (!(await input.canRead()))
      throw new Error("この音声形式を読み取れません");
    const track = await input.getPrimaryAudioTrack();
    if (!track) throw new Error("音声トラックが見つかりません");
    if (!(await track.canDecode()))
      throw new Error("この端末では入力音声のコーデックをデコードできません");
    const sampleRate = await track.getSampleRate();
    const channels = await track.getNumberOfChannels();
    const firstPacketTimestamp = await track.getFirstTimestamp();
    // AAC-in-MP4 commonly exposes an encoder priming packet at a negative
    // timestamp. The edit-list presentation timeline begins at zero; counting
    // that packet as user-visible duration both adds ~21 ms and shifts trims.
    const trackStartSeconds = Math.max(0, firstPacketTimestamp);
    const trackEndSeconds = await track.computeDuration();
    const movieDuration = await readIsoBmffMovieDuration(file);
    const presentationDuration =
      movieDuration ?? trackEndSeconds - trackStartSeconds;
    const durationFrames = Math.max(
      0,
      Math.round(presentationDuration * sampleRate),
    );
    if (durationFrames === 0) throw new Error("音声の長さが0秒です");
    return {
      fileName: file.name,
      fileSize: file.size,
      fileLastModified: file.lastModified,
      mimeType: file.type || (await input.getMimeType()),
      codec: (await track.getCodec()) ?? "unknown",
      sampleRate,
      channels,
      durationFrames,
      trackStartSeconds,
    };
  } finally {
    input.dispose();
  }
}

export function isAcceptedAudioFile(file: File): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return (
    ["m4a", "aac", "mp3", "wav", "wave"].includes(extension ?? "") ||
    file.type.startsWith("audio/")
  );
}
